// components/SpacingLayoutGuide.tsx
import { Box, Typography, Paper, Divider, Grid2 as Grid, Tooltip, IconButton } from "@mui/material";
import { ContentCopy } from "@mui/icons-material";
import { BreadcrumbNav } from "../BreadCrumbNav";
import { useNotify } from "react-admin";
import { AlertType } from "@/types";
import { useAppTheme } from "@/hooks/useAppTheme";

export const SpacingLayoutGuide = () => {
  const { theme } = useAppTheme();
  const notify = useNotify();

  const handleCopy = async (code: string) => {
    await navigator.clipboard.writeText(code);
    notify("Copied to clipboard", { type: AlertType.SUCCESS });
  };

  return (
    <Box sx={{ p: 4 }}>
      <BreadcrumbNav current="Spacing & Layout" />

      <Box mb={4}>
        <Typography variant="h1" gutterBottom>
          Spacing & Layout
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Guidelines for spacing, alignment, and layout. Follows an 8px spacing system.
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h3" gutterBottom>
            Spacing Scale
          </Typography>
          <Tooltip title="Copy spacing snippet">
            <IconButton onClick={() => handleCopy("sx={{ p: 2, m: 2 }} // Use spacing from theme.spacing")}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Use multiples of 8px for margin and padding.
        </Typography>
        <Grid container spacing={2}>
          {[8, 16, 24, 32, 40, 48].map((value) => (
            <Grid key={value}>
              <Box
                sx={{
                  width: 100,
                  height: value,
                  backgroundColor: theme.palette.primary.main,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  borderRadius: 1,
                }}
              >
                {value}px
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h3" gutterBottom>
            Card Layout Example
          </Typography>
          <Tooltip title="Copy layout example">
            <IconButton
              onClick={() =>
                handleCopy(`
<Box sx={{ p: 3 }}>
  <Typography variant="h4">Title</Typography>
  <Typography variant="body2" sx={{ mb: 1 }}>
    Description
  </Typography>
  <Typography variant="body1">Body content</Typography>
</Box>`)
              }
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Demonstrates consistent internal spacing.
        </Typography>
        <Box
          sx={{
            border: "1px dashed",
            borderColor: "divider",
            p: 3,
            borderRadius: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h4" gutterBottom>
            Card Title
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This is some description text with 8px vertical spacing below.
          </Typography>
          <Typography variant="body1">
            Here's a paragraph using 16px spacing from the title and 8px from this text to the description.
          </Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h3" gutterBottom>
            Grid Example
          </Typography>
          <Tooltip title="Copy grid example">
            <IconButton
              onClick={() =>
                handleCopy(`
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 4 }}>Item 1</Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>Item 2</Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>Item 3</Grid>
                  </Grid>
                `)
              }
            >
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Use MUI Grid layout with spacing between elements.
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 4 }}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  backgroundColor: theme.palette.grey[100],
                }}
              >
                Grid Item {i}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          Flexbox Layout Tips
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Common <code>Box</code> patterns for arranging content using flex.
        </Typography>

        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h4">Horizontal Stack</Typography>
              <Tooltip title="Copy code">
                <IconButton
                  onClick={() =>
                    handleCopy(`<Box sx={{
  display: "flex",
  gap: 2,
  backgroundColor: theme.palette.grey[100],
  p: 2,
  borderRadius: 1
}}>
  <Paper sx={{ p: 1, flex: 1 }}>Item 1</Paper>
  <Paper sx={{ p: 1, flex: 1 }}>Item 2</Paper>
  <Paper sx={{ p: 1, flex: 1 }}>Item 3</Paper>
</Box>`)
                  }
                  size="small"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                backgroundColor: theme.palette.grey[100],
                p: 2,
                borderRadius: 1,
              }}
            >
              <Paper sx={{ p: 1, flex: 1 }}>Item 1</Paper>
              <Paper sx={{ p: 1, flex: 1 }}>Item 2</Paper>
              <Paper sx={{ p: 1, flex: 1 }}>Item 3</Paper>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h4">Responsive Stack</Typography>
              <Tooltip title="Copy code">
                <IconButton
                  onClick={() =>
                    handleCopy(`<Box sx={{
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: 2,
  backgroundColor: theme.palette.grey[100],
  p: 2,
  borderRadius: 1
}}>
  <Paper sx={{ p: 1, flex: 1 }}>Left</Paper>
  <Paper sx={{ p: 1, flex: 1 }}>Right</Paper>
</Box>`)
                  }
                  size="small"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
                backgroundColor: theme.palette.grey[100],
                p: 2,
                borderRadius: 1,
              }}
            >
              <Paper sx={{ p: 1, flex: 1 }}>Left</Paper>
              <Paper sx={{ p: 1, flex: 1 }}>Right</Paper>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h4">Space-Between</Typography>
              <Tooltip title="Copy code">
                <IconButton
                  onClick={() =>
                    handleCopy(`<Box sx={{
  display: "flex",
  justifyContent: "space-between",
  backgroundColor: theme.palette.grey[100],
  p: 2,
  borderRadius: 1
}}>
  <Box>Left</Box>
  <Box>Right</Box>
</Box>`)
                  }
                  size="small"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: theme.palette.grey[100],
                p: 2,
                borderRadius: 1,
              }}
            >
              <Box>Left</Box>
              <Box>Right</Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h4">Centered Content</Typography>
              <Tooltip title="Copy code">
                <IconButton
                  onClick={() =>
                    handleCopy(`<Box sx={{
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: 100,
  backgroundColor: theme.palette.grey[100],
  borderRadius: 1
}}>
  <Typography>Centered Box</Typography>
</Box>`)
                  }
                  size="small"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 100,
                backgroundColor: theme.palette.grey[100],
                borderRadius: 1,
              }}
            >
              <Typography>Centered Box</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h3" gutterBottom>
          Spacing Do & Don’t
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Follow consistent spacing between elements to enhance clarity and visual rhythm. Use spacing tokens from the
          theme (multiples of 8px).
        </Typography>
        <Grid container spacing={4}>
          {/* DO EXAMPLE */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h4" gutterBottom>
                ✅ Do
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: 1,
                }}
              >
                <Typography variant="h2" gutterBottom>
                  Section Heading
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Use 16px spacing below headings for comfortable reading flow.
                </Typography>
                <Typography variant="body1">
                  Body text with ample spacing improves legibility and reduces visual noise.
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* DON'T EXAMPLE */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h4" gutterBottom>
                ❌ Don’t
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: 1,
                }}
              >
                <Typography variant="h2" sx={{ mb: 0 }}>
                  Section Heading
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
                  No spacing between heading and paragraph.
                </Typography>
                <Typography variant="body1">Everything feels cramped. Avoid stacking elements too closely.</Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ mt: 6 }} />
    </Box>
  );
};
